import type { AnalysisRequest, TextBlock } from '../TeoApi';
import type { TeoCore } from '../TeoCore';
import type { TextData } from '../TeoStorage';
import { TeoEventTarget } from '../utility/TeoEventTarget';
import { AIHandler } from './AIHandler';
import { AnalysisResult } from './AnalysisResult';
import { DiffMarkers } from './DiffMarkers';
import type { HistoryEntry } from './history/HistoryEntry';
import { HistoryManager } from './history/HistoryManager';
import { MarkerManager } from './MarkerManager';
import { SpellCheckHandler } from './SpellCheckHandler';
import { StyleHandler } from './StyleHandler';
import { TextUi, type TextUiState } from './TextUi';
import { WordListHandler } from './WordListHandler/WordListHandler';

export type TextEvents = {
  configChanged: (detail: AnalysisConfiguration | null) => void;
  textChanged: (detail: string) => void;
  textChangedExternal: () => void;
  gotAnalysis: (detail: AnalysisResult) => void;
  requestedAnalysis: (detail: AnalysisRequest) => void;
  analysisReadinessChanged: (detail: boolean) => void;
  analysisStateChanged: () => void;
  editable(detail: boolean): void;
  analysisPending: (detail: boolean) => void;
  historyChanged: (detail: HistoryEntry[]) => void;
};

export type AnalysisConfiguration = {
  audience: number;
  strategy: number;
  medium: number;
  is_informal: number;
};

export class TeoText {
  id: number;
  text: string = '';
  serialized: string | undefined;
  analysis: AnalysisResult | null = null;
  events = new TeoEventTarget<TextEvents>();
  core: TeoCore;
  ui: TextUi;
  getTextArray: (() => TextBlock[]) | null = null;
  ai: AIHandler;
  wordLists: WordListHandler;
  spellCheck: SpellCheckHandler;
  style: StyleHandler;
  markers: MarkerManager;
  diff: DiffMarkers;
  analysisPending = false;
  isTemporary = false;
  private _isEditable = true;
  history: HistoryManager;
  private _configuration?: Partial<AnalysisConfiguration>;
  private _readyToAnalyze = false;

  constructor(
    core: TeoCore,
    id: number,
    text: string = '',
    serialized: string | undefined = undefined,
    configuration: Partial<AnalysisConfiguration> | undefined = undefined,
    public lastEdited: number = Date.now(),
    uiState: Partial<TextUiState> = {},
    options?: {
      shouldStoreAfterCreate?: boolean;
      isTemporary?: boolean;
    },
  ) {
    const { shouldStoreAfterCreate = true, isTemporary = false } = options ?? {};
    this.text = text;
    this.serialized = serialized;
    this._configuration = configuration;
    this.id = id;
    this.core = core;
    this.ui = new TextUi({
      title: `Text ${id}`,
      ...uiState,
    });
    this.isTemporary = isTemporary;
    this.history = new HistoryManager(this);
    this.markers = new MarkerManager(this);
    this.ai = new AIHandler(this);
    this.wordLists = new WordListHandler(this);
    this.spellCheck = new SpellCheckHandler(this);
    this.style = new StyleHandler(this);
    this.diff = new DiffMarkers(this);
    this.events.addEventListener('analysisPending', (pending) => {
      this.analysisPending = pending;
      if (!this.history.viewIsActive) {
        // while history is active, the text is not editable
        this.isEditable = !pending;
      }
    });
    this.ui.events.addEventListener('textProperty', () => this.markers.syncPerformanceMarkers());
    this.ui.events.addEventListener('view', (viewName: string) => {
      if (viewName === 'performance') this.markers.syncPerformanceMarkers();
    });
    if (shouldStoreAfterCreate) {
      this.store();
    }
  }

  set configuration(config: Partial<AnalysisConfiguration> | undefined) {
    // TODO: actually do a deep value comparison here
    if (this._configuration === config) return;
    this._configuration = config;
    this.analysisConfigChanged();
    this.events.triggerEvent('configChanged', config as AnalysisConfiguration);
    this.checkReadyToAnalyze();
    this.store();
  }

  get configuration(): Partial<AnalysisConfiguration> | undefined {
    return this._configuration;
  }

  set isEditable(editable: boolean) {
    if (editable === this._isEditable) return;
    this._isEditable = editable;
    this.events.triggerEvent('editable', editable);
  }

  get isEditable(): boolean {
    return this._isEditable;
  }

  get whiteListActive(): boolean {
    return this.wordLists.whiteList.isActive;
  }

  checkReadyToAnalyze() {
    const ready = this.configIsSet() && this.textIsSet();
    if (ready !== this._readyToAnalyze) {
      this._readyToAnalyze = ready;
      this.events.triggerEvent('analysisReadinessChanged', this._readyToAnalyze);
      this.events.triggerEvent('analysisStateChanged', void 0);
    }
    return ready;
  }

  getTextData(): TextData {
    return {
      id: this.id,
      text: this.text,
      serialized: this.serialized,
      configuration: this.configuration,
      analysis: this.analysisState.isCurrent ? this.analysis?.toJSON() : undefined, // TODO: find a way to maybe restore analysis independent of the text?
      history: this.history.toJSON(),
      title: this.ui.state.title,
      lastEdited: this.lastEdited,
    };
  }

  configIsSet(): boolean {
    return !!(
      this._configuration !== undefined &&
      this._configuration.audience &&
      this._configuration.strategy &&
      this._configuration.medium &&
      this._configuration.is_informal
    );
  }

  textIsSet(): boolean {
    return this.text.length > 0;
  }

  async analyze() {
    if (!this.configIsSet()) {
      console.error('No configuration set for analysis');
      return;
    }
    const configuration = this._configuration as AnalysisConfiguration;
    let text: string | TextBlock[] = this.text;
    if (this.getTextArray !== null) {
      text = this.getTextArray();
    }
    const testRequest: AnalysisRequest = {
      text,
      ...configuration,
      use_whitelist: this.whiteListActive,
    };
    const lastInstruction = this.analysis?.currentInstruction;
    if (lastInstruction) {
      testRequest.last_instruction_id = lastInstruction.id;
      testRequest.last_text_property = lastInstruction.text_property_name;
    }
    if (this.core.dynamicConfig.options.useExpParser) {
      testRequest.use_exp_parser = true;
    }
    this.events.triggerEvent('requestedAnalysis', testRequest);
    this.events.triggerEvent('analysisPending', true);

    const [result] = await Promise.all([
      this.core.api.getAnalysis(testRequest),
      this.wordLists?.scan(),
      this.ui.state.view == 'spellcheck' ? this.spellCheck?.check() : undefined,
    ]);

    if ('tpi' in result) {
      this.analysis = new AnalysisResult(result, this, text);
      this.events.triggerEvent('analysisStateChanged', void 0);
      this.events.triggerEvent('gotAnalysis', this.analysis);
      this.events.triggerEvent('analysisPending', false);
      this.history.addHistoryEntry();
    } else {
      this.core.notifications.add({
        title: 'Analyse fehlgeschlagen',
        message: `${result.error}`,
        mode: 'error',
      });
      console.error('Error in analysis result:', result);
      this.events.triggerEvent('analysisPending', false);
    }
  }

  get analysisState() {
    return {
      isCurrent: !!this.analysis?.createdAt && this.analysis.createdAt >= this.lastEdited,
      canAnalyze: this.configIsSet() && this.textIsSet(),
    };
  }

  updateText(
    newText: string | undefined,
    serialized: string | undefined = undefined,
    isExternal: boolean = true,
  ) {
    if (newText === this.text) return;
    if (!this._isEditable) {
      console.error('Text changed while not editable');
      return;
    }
    // if (serialized !== undefined && serialized !== this.serialized) {
    this.serialized = serialized;
    // }
    if (newText !== undefined && newText !== this.text) {
      this.text = newText;
      if (isExternal) {
        this.events.triggerEvent('textChangedExternal', undefined);
      } else {
        this.events.triggerEvent('textChanged', this.text);
      }
    }
    this.lastEdited = Date.now();
    this.store();
    this.events.triggerEvent('analysisStateChanged', void 0);
    // this.events.triggerEvent('textChanged', this.text);
    this.checkReadyToAnalyze();
  }

  updateTitle(title: string) {
    this.ui.update('title', title);
    this.store();
  }

  updateTextArrayFunction(getTextArray: () => TextBlock[]) {
    this.getTextArray = getTextArray;
  }

  store() {
    if (this.isTemporary) return; // Do not store temporary texts
    this.core.storage.saveText(this.getTextData());
  }

  touch(): void {
    this.lastEdited = Date.now();
    this.events.triggerEvent('analysisStateChanged', void 0);
  }

  // TODO make this private again and handle whitelist toggle more on the configuration side
  public analysisConfigChanged() {
    if (this.analysisState.isCurrent) {
      // TODO: implement something real here
      // it depends on if analysis should be stored too, or, or, or...
      this.lastEdited = Date.now();
      this.events.triggerEvent('analysisStateChanged', void 0);
    }
  }
}
